import { FormsQuestionnaireElementInput, QuestionnaireFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  Question,
  QuestionnaireContent,
  QuestionnaireResponseContent,
  Section,
  SectionResponse,
  Translations,
} from 'modules/questionnaires/lib/types'

export const serializeQuestionnaireContent = (
  questions: QuestionnaireFragment[]
): QuestionnaireContent => {
  return questions.map((item) => {
    const isSection = item.type === 'section'
    if (isSection) {
      const element: Section = {
        uuid: item.uuid,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        questions: item.questions as Question[],
        title: item.sectionTitle ?? '',
        locked: item.locked,
        metadata: {
          scoreType: item.metadata?.scoreType,
        },
      }
      return element
    } else {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const questionInfo = item.questions[0]!
      const element: Question = {
        uuid: questionInfo.uuid,
        answerType: questionInfo.answerType,
        choiceOptions: questionInfo.choiceOptions ? questionInfo.choiceOptions : [],
        mandatory: questionInfo.mandatory,
        maxChoices: questionInfo.maxChoices,
        scale: questionInfo.scale,
        withComment: questionInfo.withComment,
        title: questionInfo.title,
      }
      return element
    }
  })
}

export const serializeQuestionnaireResponseContent = (
  questions: QuestionnaireFragment[]
): QuestionnaireResponseContent => {
  return questions.map((item) => {
    const isSection = item.type === 'section'
    if (isSection) {
      const element: SectionResponse = {
        uuid: item.uuid,
        questions: item.questions.map((question) => {
          return {
            question: {
              answerType: question.answerType,
              choiceOptions: question.choiceOptions ?? [],
              mandatory: question.mandatory,
              title: question.title,
              uuid: question.uuid,
              maxChoices: question.maxChoices,
              scale: question.scale,
              withComment: question.withComment,
            },
            answer: undefined,
          }
        }),
        title: item.sectionTitle ?? '',
        locked: Boolean(item.locked),
        metadata: {
          scoreType: item.metadata?.scoreType,
        },
      }
      return element
    } else {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const questionInfo = item.questions[0]!
      return {
        question: {
          answerType: questionInfo.answerType,
          choiceOptions: questionInfo.choiceOptions ?? [],
          mandatory: questionInfo.mandatory,
          title: questionInfo.title,
          uuid: questionInfo.uuid,
          maxChoices: questionInfo.maxChoices,
          scale: questionInfo.scale,
          withComment: questionInfo.withComment,
        },
      }
    }
  })
}

export const translations = (): Translations => {
  return {
    questionnaire: {
      add_question: i18n.t('trainings.surveys.create.add_question'),
      add_section: i18n.t('trainings.surveys.create.add_section'),
      empty_state: {
        title: i18n.t('questionnaires.questionnaire_response.empty_state.description'),
        description: '',
      },
    },
    question: {
      mandatory: i18n.t('trainings.surveys.create.question.mandatory'),
      copy_placeholder: i18n.t('trainings.surveys.create.question.copy_placeholder'),
      label: i18n.t('trainings.surveys.create.question.label'),
      description: {
        label: i18n.t('trainings.surveys.create.question.description.label'),
        placeholder: i18n.t('trainings.surveys.create.question.description.placeholder'),
      },
      multiple_choice_configurator: {
        add_option: i18n.t(
          'trainings.surveys.create.question.multiple_choice_configurator.add_option'
        ),
        placeholder: i18n.t(
          'trainings.surveys.create.question.multiple_choice_configurator.placeholder'
        ),
      },
      optional_comment: {
        label: i18n.t('trainings.surveys.create.question.optional_comment.label'),
        information: '',
      },
      placeholder: i18n.t('trainings.surveys.create.question.placeholder'),
      save: i18n.t('common.save'),
      type_selector: {
        label: i18n.t('trainings.surveys.create.question.type_selector'),
        placeholder: '',
        blank_label: '',
      },
      types: {
        number: i18n.t('trainings.surveys.create.question.types.number'),
        multiple_choice: i18n.t('trainings.surveys.create.question.types.multiple_choice'),
        text: i18n.t('trainings.surveys.create.question.types.text'),
        rating: i18n.t('trainings.surveys.create.question.types.rating'),
        single_choice: i18n.t('trainings.surveys.create.question.types.single_choice'),
      },
      actions: {
        delete: i18n.t('trainings.surveys.create.question.actions.delete'),
        duplicate: i18n.t('trainings.surveys.create.question.actions.duplicate'),
        edit: i18n.t('trainings.surveys.create.question.actions.edit'),
      },
      validations: {
        answer_type: {
          choice_options: {
            min_two_options: i18n.t(
              'trainings.surveys.create.question.validations.min_two_options'
            ),
          },
        },
        options: {
          repeated: i18n.t('trainings.surveys.create.question.validations.options.repeated'),
          required: i18n.t('trainings.surveys.create.question.validations.options.required'),
        },
        title: {
          required: i18n.t('trainings.surveys.create.question.validations.title.required'),
        },
      },
    },
    section: {
      add_question: i18n.t('trainings.surveys.create.add_question'),
      default_actions: {
        delete: i18n.t('trainings.surveys.create.question.actions.delete'),
        duplicate: i18n.t('trainings.surveys.create.question.actions.duplicate'),
        edit: i18n.t('trainings.surveys.create.question.actions.edit'),
      },
      delete_modal: {
        cancel: i18n.t('common.cancel'),
        confirm: i18n.t('common.confirm'),
        description: i18n.t('trainings.surveys.create.delete_modal.description'),
        title: i18n.t('trainings.surveys.create.delete_modal.title'),
      },
      question: i18n.t('trainings.surveys.create.question.label'),
      questions: i18n.t('trainings.surveys.create.questions'),
      questions_count: ({ count }) =>
        i18n.tp('trainings.surveys.create.questions_count', { count }),
      title: {
        copy: i18n.t('common.copy'),
        placeholder: i18n.t('trainings.surveys.create.section_placeholder'),
        save: i18n.t('common.save'),
      },
      toggle: i18n.t('trainings.surveys.create.section_toggle'),
    },
  }
}

export const serializeFormsQuestionnaireElementInput: (
  content: QuestionnaireContent
) => FormsQuestionnaireElementInput[] = (content) => {
  return content.map((item) => {
    const isSection = 'questions' in item
    return {
      type: isSection ? 'section' : 'question',
      uuid: item.uuid,
      sectionTitle: isSection ? item.title : undefined,
      questions: isSection ? serializeQuestions(item.questions) : serializeQuestions([item]),
      locked: isSection ? item.locked : undefined,
      metadata:
        isSection && item.metadata?.scoreType
          ? {
              scoreType: String(item.metadata?.scoreType),
            }
          : undefined,
    }
  })
}

const serializeQuestions = (questions: Question[]): Question[] => {
  return questions.map((question) => ({
    answerType: question.answerType,
    choiceOptions: question.choiceOptions ?? [],
    mandatory: question.mandatory,
    title: question.title,
    uuid: question.uuid,
    maxChoices: question.maxChoices,
    scale: question.scale?.map((scale) => ({
      text: scale.text,
      value: scale.value,
    })),
    withComment: question.withComment,
  }))
}
